/**
 * StatsRouter — top-level orchestrator for the v2 stats stack.
 *
 * Owns:
 *   - The state machine (SELECTION → LIVE | VIDEO → SELECTION)
 *   - All Phase 1 hooks (clock, video, roster, session, shot chart, tags, autosave, saver)
 *   - The save handler wiring
 *   - The dialog stack (config / video selection / recovery / exit confirm)
 *   - The snackbar
 *   - Mode-specific bridges (e.g. LIVE score increment from useStatsSession)
 *
 * Renders one of: ModeSelectionScreen / StatsLiveScreen / StatsVideoScreen.
 *
 * NOTE on drag-drop: react-dnd substitution is intentionally deferred to a
 * Phase 3.5 polish pass. Click-based promotion (BenchDrawer.onPromote) and
 * click-based substitution (PlayerCard X icon) provide functional parity in
 * the meantime. The original DndProvider is not mounted here so the screens
 * can be tested in isolation without the multi-backend setup.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Snackbar, Alert, Button, useMediaQuery } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';

import api from '../../../api/axiosConfig';
import { useClubTheme } from '../../../contexts/ClubThemeContext';
import { buildMuiV2Theme } from '../theme/muiV2Theme';

import {
  useAutosave,
  usePlayerRoster,
  useGameClock,
  useVideoPlayer,
  useStatsSession,
  useShotChart,
  useTags,
  useStatsSaver,
} from '../hooks';

import { STATS_MODE, MAX_ACTIVE_PLAYERS } from '../constants';
import { ACTION_CODES } from '../../utils/actionUtils';

import {
  DndStatsProvider,
  StatsCustomDragLayer,
  DraggableBenchItem,
  DraggablePlayerCard,
} from '../components/dnd';

import ModeSelectionScreen from './ModeSelectionScreen';
import StatsLiveScreen from './StatsLiveScreen';
import StatsVideoScreen from './StatsVideoScreen';

import LiveGameConfigDialog from './dialogs/LiveGameConfigDialog';
import VideoSelectionDialog from './dialogs/VideoSelectionDialog';
import RecoveryDialog from './dialogs/RecoveryDialog';
import ExitConfirmDialog from './dialogs/ExitConfirmDialog';
import TagSelectiveDialog from './dialogs/TagSelectiveDialog';

const POINTS_FOR = {
  [ACTION_CODES.TWO_PTS_MADE]: 2,
  [ACTION_CODES.THREE_PTS_MADE]: 3,
  [ACTION_CODES.FREE_THROW_MADE]: 1,
};

export default function StatsRouter({ groupeId, groupName }) {
  const { t } = useTranslation();
  const club = useClubTheme();
  const muiTheme = useMemo(
    () => buildMuiV2Theme({ accent: club.primaryColor, accentSecondary: club.secondaryColor }),
    [club.primaryColor, club.secondaryColor],
  );

  // ---------------------------------------------------------------------------
  // Top-level state machine
  // ---------------------------------------------------------------------------
  const [statsMode, setStatsMode] = useState(STATS_MODE.SELECTION);
  const [currentGame, setCurrentGame] = useState(null);
  const [gameInProgress, setGameInProgress] = useState(false);

  // Setup dialogs
  const [openConfigGameDialog, setOpenConfigGameDialog] = useState(false);
  const [openVideoSelectionDialog, setOpenVideoSelectionDialog] = useState(false);
  const [availableGames, setAvailableGames] = useState([]);
  const [gamesPagination, setGamesPagination] = useState(null);
  const [selectedVideoGame, setSelectedVideoGame] = useState(null);

  // Score (LIVE only — VIDEO reads from existing game data)
  const [puntuacionLocal, setPuntuacionLocal] = useState(0);
  const [puntuacionVisitante, setPuntuacionVisitante] = useState(0);

  // UI
  const compactLayout = useMediaQuery('(max-height:1000px), (max-width:1920px)');
  const [benchCollapsed, setBenchCollapsed] = useState(compactLayout);
  useEffect(() => { if (compactLayout) setBenchCollapsed(true); }, [compactLayout]);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [moreSheetPlayer, setMoreSheetPlayer] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info', action: null });
  const showMessage = useCallback((message, severity = 'info', action = null) => {
    setSnackbar({ open: true, message, severity, action });
  }, []);
  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  const roster = usePlayerRoster({ groupeId, initialTeamName: groupName || '' });

  const clock = useGameClock({
    enabled: true,
    statsMode,
    numberOfPeriods: currentGame?.number_of_periods || 4,
    periodDurationMinutes: currentGame?.period_duration || 10,
  });

  const video = useVideoPlayer({
    enabled: statsMode === STATS_MODE.VIDEO,
    onPeriodAutoChange: (p) => {
      if (p !== clock.currentPeriod) clock.setCurrentPeriod(p);
    },
  });

  const shotChart = useShotChart();

  const tags = useTags({ groupeId, statsMode });

  // The timestamp factory swaps based on the mode
  const computeTimestamp = useCallback(() => {
    if (statsMode === STATS_MODE.VIDEO) return video.computeActionTimestamp();
    if (statsMode === STATS_MODE.LIVE) return clock.computeActionTimestamp();
    return 0;
  }, [statsMode, video, clock]);

  const session = useStatsSession({
    statsMode,
    currentPeriod: clock.currentPeriod,
    computeTimestamp,
    t,
    onScoreChange: (delta) => setPuntuacionLocal((s) => Math.max(0, s + delta)),
    onShotAction: (ctx) => shotChart.openForAction(ctx),
    onMessage: showMessage,
  });

  const autosave = useAutosave({
    groupeId,
    t,
    onMessage: showMessage,
    statsMode,
    gameInProgress,
    actionsLog: session.actionsLog,
  });

  const saveGame = useStatsSaver({
    groupeId,
    statsMode,
    currentGame,
    videoUrl: video.videoUrl,
    selectedVideoGame,
    actionsLog: session.actionsLog,
    gameShotChartData: session.gameShotChartData,
    buildFinalTrackingForSave: roster.buildFinalTrackingForSave,
    activePlayerIds: roster.activePlayers.map((p) => p.id),
    periodSeconds: clock.periodSeconds,
    videoCurrentTime: video.videoCurrentTime,
    currentPeriod: clock.currentPeriod,
    puntuacionLocal,
    puntuacionVisitante,
    setIsSaving: autosave.setIsSaving,
    setSaveFailed: autosave.setSaveFailed,
    showMessage,
    onAfterSave: () => fullReset(),
    t,
  });

  // ---------------------------------------------------------------------------
  // IndexedDB autosave on every action change
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!gameInProgress || !groupeId || session.actionsLog.length === 0) return;
    autosave.persistGameState({
      statsMode,
      currentGame,
      actionsLog: session.actionsLog,
      activePlayers: roster.activePlayers,
      playerTimeTracking: roster.playerTimeTracking,
      currentPeriod: clock.currentPeriod,
      periodSeconds: clock.periodSeconds,
      puntuacionLocal,
      puntuacionVisitante,
      videoUrl: video.videoUrl,
      periodOffsets: video.periodOffsets,
      selectedVideoGame,
      gameInProgress,
    });
  }, [
    session.actionsLog, gameInProgress, clock.currentPeriod,
    puntuacionLocal, puntuacionVisitante, roster.activePlayers,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Mode selection flow
  // ---------------------------------------------------------------------------
  const handleSelectMode = useCallback(async (mode) => {
    setStatsMode(mode);

    if (mode === STATS_MODE.VIDEO) {
      setOpenVideoSelectionDialog(true);
      try {
        const response = await api.get(`/api/stats/games-with-stats/${groupeId}?page=1&limit=20`);
        const games = response.data.data || response.data;
        setAvailableGames(games);
        if (response.data.pagination) setGamesPagination(response.data.pagination);
      } catch (error) {
        console.error('Error loading games:', error);
        setAvailableGames([]);
      }
    } else if (mode === STATS_MODE.LIVE) {
      setOpenConfigGameDialog(true);
    }
  }, [groupeId]);

  const loadMoreGames = useCallback(async () => {
    if (!gamesPagination || gamesPagination.page >= gamesPagination.totalPages) return;
    try {
      const nextPage = gamesPagination.page + 1;
      const response = await api.get(`/api/stats/games-with-stats/${groupeId}?page=${nextPage}&limit=${gamesPagination.limit}`);
      const newGames = response.data.data || response.data;
      setAvailableGames((prev) => [...prev, ...newGames]);
      if (response.data.pagination) setGamesPagination(response.data.pagination);
    } catch (error) {
      console.error('Error loading more games:', error);
    }
  }, [gamesPagination, groupeId]);

  // ---------------------------------------------------------------------------
  // LIVE: configure new game
  // ---------------------------------------------------------------------------
  const handleConfirmLiveConfig = useCallback((game) => {
    setCurrentGame(game);
    setGameInProgress(true);
    setOpenConfigGameDialog(false);
    setPuntuacionLocal(0);
    setPuntuacionVisitante(0);
    clock.configureForNewGame(game.period_duration);
    session.reset();
  }, [clock, session]);

  // ---------------------------------------------------------------------------
  // VIDEO: pick existing or create new
  // ---------------------------------------------------------------------------
  const loadExistingActions = useCallback(async (gameId) => {
    try {
      const response = await api.get(`/api/stats/actions/${gameId}`);
      if (response.data && response.data.actions) {
        const restoredActions = response.data.actions.map((action) => ({
          playerId: action.player_id,
          playerName: roster.players.find((p) => p.id === action.player_id)?.nom || `Player ${action.player_id}`,
          action: action.action,
          actionCode: action.actionCode || action.action,
          period: action.period,
          timeSec: action.timeSec,
          videoTimestamp: action.videoTimestamp || action.timeSec,
          isVideoMode: true,
          ...(action.qualifiers ? { qualifiers: action.qualifiers } : {}),
        }));
        session.setActionsLog(restoredActions);

        let localScore = 0;
        restoredActions.forEach((a) => {
          const code = a.actionCode || a.action;
          if (POINTS_FOR[code]) localScore += POINTS_FOR[code];
        });
        setPuntuacionLocal(localScore);

        // Restore roster split + time tracking
        const activeIds = (response.data.activePlayerIds || []).map(Number);
        if (activeIds.length > 0) {
          const activeSet = new Set(activeIds);
          const active = roster.players.filter((p) => activeSet.has(Number(p.id)));
          const bench = roster.players.filter((p) => !activeSet.has(Number(p.id)));

          const rawTracking = response.data.playerTracking || {};
          // Determine the "resume" timestamp = max 'out' among active players'
          // latest videoTimeSegment (that's what was saved as the last
          // videoCurrentTime when segments were force-closed).
          let resumeVideoTime = 0;
          activeIds.forEach((pid) => {
            const segs = rawTracking[pid]?.videoTimeSegments || [];
            if (segs.length > 0) {
              const lastOut = segs[segs.length - 1].out;
              if (typeof lastOut === 'number' && lastOut > resumeVideoTime) resumeVideoTime = lastOut;
            }
          });

          const rebuiltTracking = {};
          Object.entries(rawTracking).forEach(([pidStr, trk]) => {
            const pid = Number(pidStr);
            const isActive = activeSet.has(pid);
            let realSegs = trk.realTimeSegments || [];
            let videoSegs = trk.videoTimeSegments || [];
            let realTimeIn = null;
            let videoTimeIn = null;
            let periodIn = null;

            if (isActive && videoSegs.length > 0) {
              // Re-open the last segment so time-on-floor keeps ticking
              const lastReal = realSegs[realSegs.length - 1];
              const lastVideo = videoSegs[videoSegs.length - 1];
              realSegs = realSegs.slice(0, -1);
              videoSegs = videoSegs.slice(0, -1);
              realTimeIn = lastReal?.in ?? 0;
              videoTimeIn = lastVideo?.in ?? 0;
              periodIn = lastReal?.periodIn ?? lastVideo?.periodIn ?? clock.currentPeriod;
            }

            rebuiltTracking[pid] = {
              realTimeSegments: realSegs,
              videoTimeSegments: videoSegs,
              currentlyOnFloor: isActive,
              realTimeIn,
              videoTimeIn,
              periodIn,
            };
          });

          roster.setActivePlayers(active);
          roster.setPlayers(bench);
          roster.setPlayerTimeTracking(rebuiltTracking);

          if (resumeVideoTime > 0 && typeof video.seekTo === 'function') {
            video.seekTo(resumeVideoTime);
          }
        }

        showMessage(t('estadisticasNew.actionsLoaded', { count: restoredActions.length, defaultValue: `${restoredActions.length} actions loaded` }), 'info');
      }
    } catch (error) {
      console.error('Error loading existing actions:', error);
      showMessage(t('estadisticasNew.loadActionsError', { defaultValue: 'Could not load existing actions' }), 'warning');
    }
  }, [
    roster.players, roster.setActivePlayers, roster.setPlayers, roster.setPlayerTimeTracking,
    session, showMessage, t, clock.currentPeriod, video.seekTo,
  ]);

  const requestFullscreenSilently = useCallback(() => {
    if (typeof document === 'undefined') return;
    if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen().catch(() => {});
    }
  }, []);

  const handleSelectExistingVideo = useCallback(async (game) => {
    if (!game.game.url) {
      showMessage(t('estadisticasNew.noVideoUrl', { defaultValue: 'This game has no video URL' }), 'error');
      return;
    }
    requestFullscreenSilently();
    setSelectedVideoGame(game);
    video.setVideoUrl(game.game.url);
    video.setPeriodOffsets([
      game.game.periode1 || 0,
      game.game.periode2 || 0,
      game.game.periode3 || 0,
      game.game.periode4 || 0,
    ]);
    setCurrentGame({
      game_label: game.game.game_label,
      adversary_name: game.game.adversary_name,
      number_of_periods: game.game.number_of_periods,
      period_duration: game.game.period_duration,
      game_date: game.game.game_date,
      game_location: game.game.game_location,
      existingGameId: game.game.id,
    });
    if (game.game.id) await loadExistingActions(game.game.id);
    if (game.game.score) {
      const parts = game.game.score.split('-');
      if (parts.length === 2) {
        setPuntuacionLocal(parseInt(parts[0], 10) || 0);
        setPuntuacionVisitante(parseInt(parts[1], 10) || 0);
      }
    }
    setGameInProgress(true);
    setOpenVideoSelectionDialog(false);
  }, [video, showMessage, t, loadExistingActions, requestFullscreenSilently]);

  const handleCreateNewVideo = useCallback((game, url) => {
    requestFullscreenSilently();
    video.setVideoUrl(url);
    setCurrentGame({ ...game, isNewGame: true });
    setGameInProgress(true);
    setOpenVideoSelectionDialog(false);
    session.reset();
    setPuntuacionLocal(0);
    setPuntuacionVisitante(0);
  }, [video, session, requestFullscreenSilently]);

  // ---------------------------------------------------------------------------
  // Roster swap helpers (click-based — drag-drop deferred to Phase 3.5)
  // ---------------------------------------------------------------------------
  const onPromote = useCallback((player) => {
    if (roster.activePlayers.length >= MAX_ACTIVE_PLAYERS) {
      showMessage(
        t('estadisticasNew.maxActiveReached', {
          max: MAX_ACTIVE_PLAYERS,
          defaultValue: `${MAX_ACTIVE_PLAYERS} players max on the floor — substitute one first`,
        }),
        'warning',
      );
      return;
    }
    roster.moveBenchToFloor(player);
    roster.checkIn(player.id, {
      periodSeconds: clock.periodSeconds,
      videoCurrentTime: video.videoCurrentTime,
      currentPeriod: clock.currentPeriod,
    });
  }, [roster, clock, video, showMessage, t]);

  const onSubstitute = useCallback((player) => {
    roster.checkOut(player.id, {
      periodSeconds: clock.periodSeconds,
      videoCurrentTime: video.videoCurrentTime,
      currentPeriod: clock.currentPeriod,
    });
    roster.moveFloorToBench(player);
  }, [roster, clock, video]);

  // ---------------------------------------------------------------------------
  // Drag-drop swap (Phase 3.5)
  //
  // Both DraggableBenchItem and DraggablePlayerCard call onSwap with the
  // same argument order: (benchPlayer, floorPlayer). Whichever side
  // initiated the drag, the result is identical: bench ↔ floor swap.
  // ---------------------------------------------------------------------------
  const onSwapBenchFloor = useCallback((benchPlayer, floorPlayer) => {
    if (!benchPlayer || !floorPlayer || benchPlayer.id === floorPlayer.id) return;

    const ctx = {
      periodSeconds: clock.periodSeconds,
      videoCurrentTime: video.videoCurrentTime,
      currentPeriod: clock.currentPeriod,
    };

    // Floor player goes to bench
    roster.checkOut(floorPlayer.id, ctx);
    roster.moveFloorToBench(floorPlayer);

    // Bench player goes to floor
    roster.moveBenchToFloor(benchPlayer);
    roster.checkIn(benchPlayer.id, ctx);
  }, [roster, clock, video]);

  // Render-prop factories used by BenchDrawer / ActionsPanel to inject the
  // dnd wrappers without leaking dnd concerns into the components themselves.
  const renderBenchItem = useCallback((player, { collapsed }) => (
    <DraggableBenchItem
      player={player}
      collapsed={collapsed}
      onPromote={onPromote}
      onSwap={onSwapBenchFloor}
    />
  ), [onPromote, onSwapBenchFloor]);

  const renderActiveCard = useCallback((player, cardProps, inner) => (
    <DraggablePlayerCard
      {...cardProps}
      onSwap={onSwapBenchFloor}
    >
      {inner}
    </DraggablePlayerCard>
  ), [onSwapBenchFloor]);

  // ---------------------------------------------------------------------------
  // Derived: foul count per player (game-wide).
  // Counts ACTION_CODES.FOUL + ACTION_CODES.TECHNICAL committed by each player.
  // FOUL_RECEIVED is fouls drawn, not committed → excluded.
  // ---------------------------------------------------------------------------
  const foulsByPlayerId = useMemo(() => {
    const map = {};
    for (const action of session.actionsLog) {
      const code = action.actionCode || action.action;
      if (code === ACTION_CODES.FOUL || code === ACTION_CODES.TECHNICAL) {
        map[action.playerId] = (map[action.playerId] || 0) + 1;
      }
    }
    return map;
  }, [session.actionsLog]);

  // ---------------------------------------------------------------------------
  // Derived: time on floor per player.
  //
  // Sums the closed segments AND adds the still-open segment for any player
  // currently on the floor. Mode-aware:
  //   - LIVE  → uses realTimeSegments + (clock.periodSeconds - realTimeIn)
  //   - VIDEO → uses videoTimeSegments + (videoCurrentTime - videoTimeIn)
  // ---------------------------------------------------------------------------
  const timeOnFloorByPlayerId = useMemo(() => {
    const result = {};
    const useVideo = statsMode === STATS_MODE.VIDEO;
    const now = useVideo ? video.videoCurrentTime : clock.periodSeconds;

    Object.entries(roster.playerTimeTracking).forEach(([pid, tracking]) => {
      const segments = useVideo ? tracking.videoTimeSegments : tracking.realTimeSegments;
      let total = (segments || []).reduce((sum, seg) => sum + Math.max(0, seg.out - seg.in), 0);

      if (tracking.currentlyOnFloor) {
        const openIn = useVideo ? tracking.videoTimeIn : tracking.realTimeIn;
        if (typeof openIn === 'number') {
          total += Math.max(0, now - openIn);
        }
      }
      result[pid] = total;
    });
    return result;
  }, [roster.playerTimeTracking, statsMode, clock.periodSeconds, video.videoCurrentTime]);

  // ---------------------------------------------------------------------------
  // Tag selective dialog — apply handler
  // ---------------------------------------------------------------------------
  const handleApplyTagToSelected = useCallback(() => {
    const selectedTag = tags.selectedTag;
    if (!selectedTag || tags.selectedPlayersForTag.length === 0) {
      showMessage(t('estadisticasNew.selectTagAndPlayer', { defaultValue: 'Select a tag and at least one player' }), 'warning');
      return;
    }

    if (statsMode === STATS_MODE.LIVE && !clock.periodStarted) {
      showMessage(t('estadisticasNew.startPeriodFirst', { defaultValue: 'Start the period first' }), 'warning');
      return;
    }

    const allPlayers = [...roster.activePlayers, ...roster.players];
    const tagActions = tags.selectedPlayersForTag
      .map((pid) => {
        const player = allPlayers.find((p) => p.id === pid);
        if (!player) return null;
        return tags.buildTagAction(selectedTag, player, {
          currentPeriod: clock.currentPeriod,
          mode: statsMode,
          videoCurrentTime: video.videoCurrentTime,
          videoOffset: video.videoOffset,
          realTime: clock.realTimeRef.current,
        });
      })
      .filter(Boolean);

    if (tagActions.length > 0) {
      session.setActionsLog((prev) => [...prev, ...tagActions]);
      session.setLastAction(null);
      showMessage(
        t('estadisticasNew.tagApplied', {
          tag: selectedTag.libelle,
          count: tagActions.length,
          defaultValue: `Tag "${selectedTag.libelle}" applied to ${tagActions.length} player(s)`,
        }),
        'success',
      );
    }
    tags.closeDialog();
  }, [tags, statsMode, clock, video, roster, session, showMessage, t]);

  // ---------------------------------------------------------------------------
  // Tag click bridge → useTags handles single/double click
  // ---------------------------------------------------------------------------
  const handleTagClick = useCallback((tag) => {
    tags.handleTagClick(tag, {
      onSingleClick: (clickedTag) => {
        // Apply tag to all players (active + bench)
        const allPlayers = [...roster.activePlayers, ...roster.players];
        if (statsMode === STATS_MODE.LIVE && !clock.periodStarted) {
          showMessage(t('estadisticasNew.startPeriodFirst', { defaultValue: 'Start the period first' }), 'warning');
          return;
        }
        const tagActions = allPlayers
          .map((player) => tags.buildTagAction(clickedTag, player, {
            currentPeriod: clock.currentPeriod,
            mode: statsMode,
            videoCurrentTime: video.videoCurrentTime,
            videoOffset: video.videoOffset,
            realTime: clock.realTimeRef.current,
          }))
          .filter(Boolean);
        if (tagActions.length > 0) {
          session.setActionsLog((prev) => [...prev, ...tagActions]);
          session.setLastAction(null);
          showMessage(
            t('estadisticasNew.tagAppliedToTeam', { tag: clickedTag.libelle, count: tagActions.length, defaultValue: `Tag "${clickedTag.libelle}" applied to ${tagActions.length} players` }),
            'success',
          );
        }
      },
      onDoubleClick: (clickedTag) => {
        tags.openSelectiveDialog(clickedTag);
      },
    });
  }, [tags, roster, statsMode, clock, video, session, showMessage, t]);

  // ---------------------------------------------------------------------------
  // Score handlers
  // ---------------------------------------------------------------------------
  const onLocalIncrement = useCallback(() => {
    if (statsMode === STATS_MODE.VIDEO) return;
    setPuntuacionLocal((s) => s + 1);
  }, [statsMode]);
  const onLocalDecrement = useCallback(() => {
    if (statsMode === STATS_MODE.VIDEO) return;
    setPuntuacionLocal((s) => Math.max(0, s - 1));
  }, [statsMode]);
  const onOpponentIncrement = useCallback(() => {
    if (statsMode === STATS_MODE.VIDEO) return;
    setPuntuacionVisitante((s) => s + 1);
  }, [statsMode]);
  const onOpponentDecrement = useCallback(() => {
    if (statsMode === STATS_MODE.VIDEO) return;
    setPuntuacionVisitante((s) => Math.max(0, s - 1));
  }, [statsMode]);
  const onOpponentQuickScore = useCallback((code, pts) => {
    setPuntuacionVisitante((s) => s + pts);
    session.recordOpponentScore(code);
  }, [session]);

  // ---------------------------------------------------------------------------
  // Reset / exit
  // ---------------------------------------------------------------------------
  const fullReset = useCallback(() => {
    setStatsMode(STATS_MODE.SELECTION);
    setCurrentGame(null);
    setGameInProgress(false);
    setSelectedVideoGame(null);
    setPuntuacionLocal(0);
    setPuntuacionVisitante(0);
    setOpenConfigGameDialog(false);
    setOpenVideoSelectionDialog(false);
    setMoreSheetOpen(false);
    setMoreSheetPlayer(null);
    autosave.clearGameState();
    clock.reset();
    video.reset();
    roster.reset();
    session.reset();
    shotChart.reset();
    tags.reset();
  }, [autosave, clock, video, roster, session, shotChart, tags]);

  const handleExit = useCallback(() => {
    if (autosave.hasUnsavedData()) {
      autosave.requestExitConfirmation(fullReset);
    } else {
      fullReset();
    }
  }, [autosave, fullReset]);

  // ---------------------------------------------------------------------------
  // Recovery
  // ---------------------------------------------------------------------------
  const handleRestoreRecovery = useCallback(() => {
    const s = autosave.acceptRecovery();
    if (!s) return;
    setStatsMode(s.statsMode);
    setCurrentGame(s.currentGame);
    session.setActionsLog(s.actionsLog || []);
    roster.setActivePlayers(s.activePlayers || []);
    roster.setPlayerTimeTracking(s.playerTimeTracking || {});
    clock.setCurrentPeriod(s.currentPeriod || 1);
    clock.setPeriodSeconds(s.periodSeconds || 0);
    setPuntuacionLocal(s.puntuacionLocal || 0);
    setPuntuacionVisitante(s.puntuacionVisitante || 0);
    video.setVideoUrl(s.videoUrl || '');
    video.setPeriodOffsets(s.periodOffsets || [0, 0, 0, 0]);
    if (s.selectedVideoGame) setSelectedVideoGame(s.selectedVideoGame);
    setGameInProgress(true);
    showMessage(t('estadisticasNew.gameRecovered', { defaultValue: 'Session restored' }), 'success');
  }, [autosave, session, roster, clock, video, showMessage, t]);

  // ---------------------------------------------------------------------------
  // Memoized props per screen to keep render light
  // ---------------------------------------------------------------------------
  const liveScreenProps = useMemo(() => ({
    currentGame,
    teamName: roster.teamName,
    players: roster.players,
    activePlayers: roster.activePlayers,
    playerTimeTracking: roster.playerTimeTracking,
    timeOnFloorByPlayerId,
    foulsByPlayerId,
    onPromote,
    onSubstitute,

    currentPeriod: clock.currentPeriod,
    timeLeft: clock.timeLeft,
    isRunning: clock.isRunning,
    periodStarted: clock.periodStarted,
    onStart: clock.startPeriod,
    onPause: clock.pausePeriod,
    onEnd: clock.endPeriod,

    puntuacionLocal,
    puntuacionVisitante,
    onLocalIncrement,
    onLocalDecrement,
    onOpponentIncrement,
    onOpponentDecrement,
    onOpponentQuickScore,

    onAction: session.recordAction,
    onUndo: session.undoLastAction,
    hasLastAction: !!session.lastAction,

    tags: tags.availableTags,
    activeQualifiers: session.activeQualifiers,
    onTagClick: handleTagClick,
    onToggleQualifier: session.toggleQualifier,

    moreSheetOpen,
    moreSheetPlayer,
    onOpenMoreSheet: (p) => { setMoreSheetPlayer(p); setMoreSheetOpen(true); },
    onCloseMoreSheet: () => setMoreSheetOpen(false),
    onMoreSheetAction: (player, code) => session.recordAction(player, code, 0),

    benchCollapsed,
    onToggleBench: () => setBenchCollapsed((c) => !c),

    renderBenchItem,
    renderActiveCard,

    isOnline: autosave.isOnline,
    isSaving: autosave.isSaving,
    saveFailed: autosave.saveFailed,
    onSave: saveGame,
    onExit: handleExit,
    t,
  }), [
    currentGame, roster, clock, puntuacionLocal, puntuacionVisitante,
    session, tags.availableTags, handleTagClick, moreSheetOpen, moreSheetPlayer,
    benchCollapsed, autosave.isOnline, autosave.isSaving, autosave.saveFailed,
    saveGame, handleExit,
    onPromote, onSubstitute, onLocalIncrement, onLocalDecrement,
    onOpponentIncrement, onOpponentDecrement, onOpponentQuickScore,
    renderBenchItem, renderActiveCard,
    foulsByPlayerId, timeOnFloorByPlayerId, t,
  ]);

  const videoScreenProps = useMemo(() => ({
    currentGame,
    teamName: roster.teamName,
    players: roster.players,
    activePlayers: roster.activePlayers,
    playerTimeTracking: roster.playerTimeTracking,
    timeOnFloorByPlayerId,
    foulsByPlayerId,
    onPromote,
    onSubstitute,

    playerRef: video.playerRef,
    videoUrl: video.videoUrl,
    videoPlaying: video.videoPlaying,
    videoDuration: video.videoDuration,
    videoCurrentTime: video.videoCurrentTime,
    playbackRate: video.playbackRate,
    videoOffset: video.videoOffset,
    periodOffsets: video.periodOffsets,
    onProgress: video.handleProgress,
    onDuration: video.handleDuration,
    onTogglePlay: video.togglePlay,
    onSeek: video.seekTo,
    onSkip: video.skip,
    onPlaybackRateChange: video.setPlaybackRate,
    onOffsetChange: video.setVideoOffset,

    currentPeriod: clock.currentPeriod,
    puntuacionLocal,
    puntuacionVisitante,
    onOpponentQuickScore,

    actionsLog: session.actionsLog,
    onAction: session.recordAction,
    onUndo: session.undoLastAction,
    hasLastAction: !!session.lastAction,

    tags: tags.availableTags,
    activeQualifiers: session.activeQualifiers,
    onTagClick: handleTagClick,
    onToggleQualifier: session.toggleQualifier,

    moreSheetOpen,
    moreSheetPlayer,
    onOpenMoreSheet: (p) => { setMoreSheetPlayer(p); setMoreSheetOpen(true); },
    onCloseMoreSheet: () => setMoreSheetOpen(false),
    onMoreSheetAction: (player, code) => session.recordAction(player, code, 0),

    shotChartOpen: shotChart.shotChartOpen,
    shotChartAction: shotChart.shotChartAction,
    onShotChartRecorded: (shot) => {
      session.recordShotChart(shot, shotChart.shotChartAction);
      shotChart.close();
    },
    onShotChartClose: shotChart.close,
    gameShotChartData: session.gameShotChartData,

    benchCollapsed,
    onToggleBench: () => setBenchCollapsed((c) => !c),

    renderBenchItem,
    renderActiveCard,

    isOnline: autosave.isOnline,
    isSaving: autosave.isSaving,
    saveFailed: autosave.saveFailed,
    onSave: saveGame,
    onExit: handleExit,
    t,
  }), [
    currentGame, roster, video, clock.currentPeriod,
    puntuacionLocal, puntuacionVisitante, session,
    tags.availableTags, handleTagClick, moreSheetOpen, moreSheetPlayer,
    shotChart, benchCollapsed,
    autosave.isOnline, autosave.isSaving, autosave.saveFailed,
    saveGame, handleExit, onPromote, onSubstitute, onOpponentQuickScore,
    renderBenchItem, renderActiveCard,
    foulsByPlayerId, timeOnFloorByPlayerId, t,
  ]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <ThemeProvider theme={muiTheme}>
    <DndStatsProvider>
      <StatsCustomDragLayer />
      {statsMode === STATS_MODE.SELECTION && (
        <ModeSelectionScreen onSelectMode={handleSelectMode} t={t} />
      )}

      {statsMode === STATS_MODE.LIVE && currentGame && (
        <StatsLiveScreen {...liveScreenProps} />
      )}

      {statsMode === STATS_MODE.VIDEO && currentGame && (
        <StatsVideoScreen {...videoScreenProps} />
      )}

      {/* Setup dialogs */}
      <LiveGameConfigDialog
        open={openConfigGameDialog}
        onClose={() => { setOpenConfigGameDialog(false); setStatsMode(STATS_MODE.SELECTION); }}
        onConfirm={handleConfirmLiveConfig}
        t={t}
      />
      <VideoSelectionDialog
        open={openVideoSelectionDialog}
        onClose={() => { setOpenVideoSelectionDialog(false); setStatsMode(STATS_MODE.SELECTION); }}
        availableGames={availableGames}
        onLoadMore={loadMoreGames}
        hasMoreGames={!!gamesPagination && gamesPagination.page < gamesPagination.totalPages}
        onSelectExisting={handleSelectExistingVideo}
        onCreateNew={handleCreateNewVideo}
        t={t}
      />

      {/* Recovery + exit */}
      <RecoveryDialog
        open={autosave.recoveryDialogOpen}
        recoveryData={autosave.recoveryData}
        onRestore={handleRestoreRecovery}
        onDiscard={autosave.discardRecovery}
        t={t}
      />
      <ExitConfirmDialog
        open={autosave.openExitConfirmDialog}
        onCancel={autosave.cancelExit}
        onConfirm={autosave.confirmExit}
        t={t}
      />

      <TagSelectiveDialog
        open={tags.openTagsDialog}
        tag={tags.selectedTag}
        players={[...roster.activePlayers, ...roster.players]}
        selectedPlayerIds={tags.selectedPlayersForTag}
        onTogglePlayer={tags.togglePlayerForTag}
        onSelectAll={tags.selectAllPlayersForTag}
        onConfirm={handleApplyTagToSelected}
        onClose={tags.closeDialog}
        t={t}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={closeSnackbar}
          action={
            snackbar.action ? (
              <Button color="inherit" size="small" onClick={() => { snackbar.action(); closeSnackbar(); }}>
                {t('estadisticasNew.undo', { defaultValue: 'Undo' })}
              </Button>
            ) : null
          }
          sx={{ minWidth: 280 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </DndStatsProvider>
    </ThemeProvider>
  );
}
